// modules/VehicleEquipmentSystem.js
// 战车装备系统模块
import InventoryHelper from './InventoryHelper.js';
import ValidationHelper from './ValidationHelper.js';
import DebugHelper from './DebugHelper.js';
import { deepClone } from './utils/PlayerDataSync.js';

export default class VehicleEquipmentSystem {
    /**
     * 战车装备槽位定义
     */
    static SLOTS = {
        MAIN_CANNON: 'mainCannon',    // 主炮
        SUB_CANNON: 'subCannon',     // 副炮
        SE: 'se',                     // S-E
        AI: 'ai',                     // AI装置
        ENGINE: 'engine',             // 发动机
        CHASSIS: 'chassis'            // 底盘
    };
    
    /**
     * 必须装备槽位（必须装备才能移动和上车）
     */
    static REQUIRED_SLOTS = [
        'ai',      // AI装置
        'engine',  // 发动机
        'chassis'  // 底盘
    ];
    
    /**
     * 装备槽位顺序（用于显示）
     */
    static SLOT_ORDER = [
        'mainCannon',  // 主炮
        'subCannon',   // 副炮
        'se',          // S-E
        'ai',          // AI装置
        'engine',      // 发动机
        'chassis'      // 底盘
    ];
    
    /**
     * 槽位显示名称
     */
    static SLOT_NAMES = {
        'mainCannon': '主炮',
        'subCannon': '副炮',
        'se': 'S-E',
        'ai': 'AI装置',
        'engine': '发动机',
        'chassis': '底盘'
    };
    
    /**
     * 获取发动机的载重
     * @param {Object} vehicle - 战车对象
     * @returns {number} 载重值，如果没有发动机则返回0
     */
    static getLoadCapacity(vehicle) {
        if (!vehicle || !vehicle.equipment || !vehicle.equipment.engine) {
            return 0;
        }
        
        const engine = vehicle.equipment.engine;
        // 检查装备是否损坏
        if (engine.durability !== undefined && engine.durability <= 0) {
            return 0;
        }
        
        return engine.loadCapacity || 0;
    }
    
    /**
     * 计算当前装备的总重量
     * @param {Object} vehicle - 战车对象
     * @returns {number} 总重量
     */
    static calculateTotalWeight(vehicle) {
        if (!vehicle || !vehicle.equipment) {
            return 0;
        }
        
        let totalWeight = 0;
        
        // 遍历所有装备，累加重量
        for (const slot in vehicle.equipment) {
            const equipment = vehicle.equipment[slot];
            if (!equipment) continue;
            
            // 跳过损坏的装备（损坏的装备不计算重量，因为已经卸下了）
            if (equipment.durability !== undefined && equipment.durability <= 0) {
                continue;
            }
            
            // 累加重量（如果没有weight字段，默认为0）
            if (equipment.weight !== undefined) {
                totalWeight += equipment.weight;
            }
        }
        
        return totalWeight;
    }
    
    /**
     * 检查装备是否可以装备（基于重量限制）
     * @param {Object} item - 要装备的物品
     * @param {Object} vehicle - 战车对象
     * @param {string} slot - 装备槽位
     * @returns {Object} 检查结果 {canEquip: boolean, message: string, currentWeight?: number, loadCapacity?: number, newWeight?: number}
     */
    static checkWeightLimit(item, vehicle, slot) {
        // 获取发动机载重
        const loadCapacity = this.getLoadCapacity(vehicle);
        
        // 如果没有发动机，无法限制重量（但应该有发动机才能装备，所以这里返回允许）
        if (loadCapacity === 0) {
            return {
                canEquip: true,
                message: '无载重限制（无发动机）',
                currentWeight: 0,
                loadCapacity: 0
            };
        }
        
        // 计算当前装备重量（不包括要替换的装备）
        let currentEquipmentWeight = 0;
        for (const equipmentSlot in vehicle.equipment) {
            const equipment = vehicle.equipment[equipmentSlot];
            if (!equipment) continue;
            
            // 如果是当前槽位且要替换，跳过当前装备的重量
            if (equipmentSlot === slot) {
                continue;
            }
            
            // 跳过损坏的装备
            if (equipment.durability !== undefined && equipment.durability <= 0) {
                continue;
            }
            
            if (equipment.weight !== undefined) {
                currentEquipmentWeight += equipment.weight;
            }
        }
        
        // 计算SP对应的重量（1SP=0.1重量）
        const spWeight = (vehicle.sp || 0) / 10;
        
        // 计算新装备后的总重量（装备重量 + SP重量 + 新装备重量）
        const itemWeight = item.weight || 0;
        const newTotalWeight = currentEquipmentWeight + spWeight + itemWeight;
        
        // 检查是否超过载重
        if (newTotalWeight > loadCapacity) {
            return {
                canEquip: false,
                message: `总重量 ${newTotalWeight.toFixed(1)} 超过发动机载重 ${loadCapacity}（装备：${currentEquipmentWeight}，SP：${spWeight.toFixed(1)}，新装备：${itemWeight}）`,
                currentWeight: currentEquipmentWeight + spWeight,
                loadCapacity: loadCapacity,
                newWeight: newTotalWeight
            };
        }
        
        return {
            canEquip: true,
            message: '载重检查通过',
            currentWeight: currentEquipmentWeight + spWeight,
            loadCapacity: loadCapacity,
            newWeight: newTotalWeight
        };
    }
    
    /**
     * 穿戴战车装备
     * @param {Object} item - 装备对象
     * @param {Object} vehicle - 战车对象
     * @returns {Object} 穿戴结果 {success: boolean, message: string, oldEquipment?: Object}
     */
    static equipItem(item, vehicle) {
        // 验证数据
        const itemValidation = ValidationHelper.validateGameData(item, '战车装备');
        const vehicleValidation = ValidationHelper.validateGameData(vehicle, '战车');
        
        if (!itemValidation.valid || !vehicleValidation.valid) {
            return {
                success: false,
                message: itemValidation.message || vehicleValidation.message || '装备或战车数据无效'
            };
        }
        
        // 检查是否为战车装备类型
        if (item.type !== 'vehicle_equipment') {
            return {
                success: false,
                message: '该物品不是战车装备'
            };
        }
        
        // 获取装备槽位
        const slot = item.slot;
        if (!slot) {
            return {
                success: false,
                message: '无法识别装备槽位'
            };
        }
        
        // 确保装备对象存在
        if (!vehicle.equipment) {
            vehicle.equipment = {
                mainCannon: null,
                subCannon: null,
                se: null,
                ai: null,
                engine: null,
                chassis: null
            };
        }
        
        // 检查槽位是否有效
        if (!vehicle.equipment.hasOwnProperty(slot)) {
            return {
                success: false,
                message: `无效的装备槽位: ${slot}`
            };
        }
        
        // 查找当前槽位的装备
        const currentEquipped = vehicle.equipment[slot];
        let oldEquipment = null;
        
        if (currentEquipped) {
            // 卸下当前装备
            oldEquipment = { ...currentEquipped };
            vehicle.equipment[slot] = null;
        }
        
        // 检查装备是否损坏
        if (item.durability !== undefined && item.durability <= 0) {
            return {
                success: false,
                message: `${item.name} 已损坏，无法装备`
            };
        }
        
        // 检查重量限制（只有在不是发动机槽位时才检查，因为发动机本身决定载重）
        // 但如果要装备发动机，需要检查新发动机的载重是否足够
        if (slot !== 'engine') {
            const weightCheck = this.checkWeightLimit(item, vehicle, slot);
            if (!weightCheck.canEquip) {
                // 恢复旧装备（如果有）
                if (oldEquipment) {
                    vehicle.equipment[slot] = oldEquipment;
                }
                return {
                    success: false,
                    message: weightCheck.message
                };
            }
        } else {
            // 如果装备的是发动机，需要检查新发动机的载重是否足够承载当前所有其他装备
            const newEngine = item;
            const loadCapacity = newEngine.loadCapacity || 0;
            
            if (loadCapacity > 0) {
                // 计算除了发动机之外的所有装备重量
                let otherEquipmentWeight = 0;
                for (const equipmentSlot in vehicle.equipment) {
                    if (equipmentSlot === 'engine') continue; // 跳过发动机
                    const equipment = vehicle.equipment[equipmentSlot];
                    if (!equipment) continue;
                    if (equipment.durability !== undefined && equipment.durability <= 0) continue;
                    if (equipment.weight !== undefined) {
                        otherEquipmentWeight += equipment.weight;
                    }
                }
                
                // 计算SP对应的重量（1SP=0.1重量）
                const spWeight = (vehicle.sp || 0) / 10;
                
                // 加上新发动机自身的重量
                const newEngineWeight = newEngine.weight || 0;
                const totalWeight = otherEquipmentWeight + spWeight + newEngineWeight;
                
                if (totalWeight > loadCapacity) {
                    // 恢复旧装备（如果有）
                    if (oldEquipment) {
                        vehicle.equipment[slot] = oldEquipment;
                    }
                    return {
                        success: false,
                        message: `总重量 ${totalWeight.toFixed(1)} 超过新发动机载重 ${loadCapacity}（其他装备：${otherEquipmentWeight}，SP：${spWeight.toFixed(1)}，新发动机：${newEngineWeight}）`
                    };
                }
            }
        }
        
        // 穿戴新装备（深拷贝避免引用问题）
        const equippedItem = deepClone(item);
        vehicle.equipment[slot] = equippedItem;
        
        // 如果装备的是底盘，更新最大抗腐蚀图层值
        if (slot === 'chassis') {
            vehicle.maxCorrosionLayer = this.calculateMaxCorrosionLayer(vehicle);
            // 如果当前图层值超过新的最大值，则调整到最大值
            if (vehicle.corrosionLayer > vehicle.maxCorrosionLayer) {
                vehicle.corrosionLayer = vehicle.maxCorrosionLayer;
            }
        }
        
        DebugHelper.log(`VehicleEquipmentSystem: 已装备 ${item.name} 到 ${this.SLOT_NAMES[slot] || slot}`);
        
        return {
            success: true,
            message: `已装备 ${item.name}`,
            oldEquipment: oldEquipment
        };
    }
    
    /**
     * 卸下战车装备
     * @param {Object} vehicle - 战车对象
     * @param {string} slot - 装备槽位
     * @returns {Object} 卸下结果 {success: boolean, message: string, item?: Object}
     */
    static unequipItem(vehicle, slot) {
        if (!vehicle || !slot) {
            return {
                success: false,
                message: '参数无效'
            };
        }
        
        if (!vehicle.equipment) {
            return {
                success: false,
                message: '战车没有装备'
            };
        }
        
        // 检查是否为必须装备
        if (this.REQUIRED_SLOTS.includes(slot)) {
            return {
                success: false,
                message: `${this.SLOT_NAMES[slot] || slot} 是必须装备，无法卸下`
            };
        }
        
        const equippedItem = vehicle.equipment[slot];
        if (!equippedItem) {
            return {
                success: false,
                message: '该槽位没有装备'
            };
        }
        
        // 卸下装备
        vehicle.equipment[slot] = null;
        
        // 如果卸下的是底盘，重置最大抗腐蚀图层值
        if (slot === 'chassis') {
            vehicle.maxCorrosionLayer = 0;
            vehicle.corrosionLayer = 0;
        }
        
        DebugHelper.log(`VehicleEquipmentSystem: 已卸下 ${equippedItem.name}`);
        
        return {
            success: true,
            message: `已卸下 ${equippedItem.name}`,
            item: equippedItem
        };
    }
    
    /**
     * 获取指定槽位的装备
     * @param {Object} vehicle - 战车对象
     * @param {string} slot - 装备槽位
     * @returns {Object|null} 装备对象或null
     */
    static getEquippedItem(vehicle, slot) {
        if (!vehicle || !vehicle.equipment || !slot) {
            return null;
        }
        
        return vehicle.equipment[slot] || null;
    }
    
    /**
     * 检查必须装备是否齐全
     * @param {Object} vehicle - 战车对象
     * @returns {Object} 检查结果 {valid: boolean, missing: Array<string>, message: string}
     */
    static checkRequiredEquipment(vehicle) {
        if (!vehicle || !vehicle.equipment) {
            return {
                valid: false,
                missing: this.REQUIRED_SLOTS,
                message: '战车没有装备系统'
            };
        }
        
        const missing = [];
        
        for (const slot of this.REQUIRED_SLOTS) {
            const equipment = vehicle.equipment[slot];
            if (!equipment) {
                missing.push(slot);
            } else {
                // 检查装备是否损坏
                if (equipment.durability !== undefined && equipment.durability <= 0) {
                    missing.push(slot);
                }
            }
        }
        
        if (missing.length > 0) {
            const missingNames = missing.map(slot => this.SLOT_NAMES[slot] || slot);
            return {
                valid: false,
                missing: missing,
                message: `缺少必须装备: ${missingNames.join(', ')}`
            };
        }
        
        return {
            valid: true,
            missing: [],
            message: '必须装备齐全'
        };
    }
    
    /**
     * 检查战车是否可以移动（返回详细信息）
     * @param {Object} vehicle - 战车对象
     * @returns {Object} 检查结果 {canMove: boolean, message: string}
     */
    static checkCanMove(vehicle) {
        // 检查必须装备
        const check = this.checkRequiredEquipment(vehicle);
        if (!check.valid) {
            return {
                canMove: false,
                message: check.message
            };
        }
        
        // 检查重量限制（包括装备重量和SP重量）
        const totalWeight = this.calculateTotalWeightWithSp(vehicle);
        const loadCapacity = this.getLoadCapacity(vehicle);
        
        // 如果有载重限制且超过载重，不能移动
        if (loadCapacity > 0 && totalWeight > loadCapacity) {
            const equipmentWeight = this.calculateTotalWeight(vehicle);
            const spWeight = (vehicle.sp || 0) / 10;
            return {
                canMove: false,
                message: `战车超重！总重量 ${totalWeight.toFixed(1)} 超过发动机载重 ${loadCapacity}（装备：${equipmentWeight}，SP：${spWeight.toFixed(1)}）`
            };
        }
        
        return {
            canMove: true,
            message: '可以移动'
        };
    }
    
    /**
     * 检查战车是否可以移动
     * @param {Object} vehicle - 战车对象
     * @returns {boolean} 是否可以移动
     */
    static canMove(vehicle) {
        const check = this.checkCanMove(vehicle);
        return check.canMove;
    }
    
    /**
     * 检查战车是否可以上车
     * @param {Object} vehicle - 战车对象
     * @returns {Object} 检查结果 {canBoard: boolean, message: string}
     */
    static canBoard(vehicle) {
        // 检查必须装备
        const check = this.checkRequiredEquipment(vehicle);
        
        if (!check.valid) {
            return {
                canBoard: false,
                message: check.message
            };
        }
        
        // 检查重量限制（包括装备重量和SP重量）
        const totalWeight = this.calculateTotalWeightWithSp(vehicle);
        const loadCapacity = this.getLoadCapacity(vehicle);
        
        // 如果有载重限制且超过载重，不能上车
        if (loadCapacity > 0 && totalWeight > loadCapacity) {
            const equipmentWeight = this.calculateTotalWeight(vehicle);
            const spWeight = (vehicle.sp || 0) / 10;
            return {
                canBoard: false,
                message: `战车超重！总重量 ${totalWeight.toFixed(1)} 超过发动机载重 ${loadCapacity}（装备：${equipmentWeight}，SP：${spWeight.toFixed(1)}）`
            };
        }
        
        return {
            canBoard: true,
            message: '可以上车'
        };
    }
    
    /**
     * 计算战车SP最大值（基于发动机载重和装备重量）
     * @param {Object} vehicle - 战车对象
     * @returns {number} SP最大值（(载重 - 装备重量) * 10，1SP=0.1重量）
     */
    static calculateMaxSp(vehicle) {
        const loadCapacity = this.getLoadCapacity(vehicle);
        if (loadCapacity <= 0) {
            return 0;
        }
        // 计算装备总重量
        const equipmentWeight = this.calculateTotalWeight(vehicle);
        // SP最大值 = (发动机载重 - 所有战车装备重量) * 10（1SP = 0.1重量）
        const remainingCapacity = loadCapacity - equipmentWeight;
        return remainingCapacity > 0 ? remainingCapacity * 10 : 0;
    }
    
    /**
     * 计算战车总重量（包括装备重量和SP重量）
     * @param {Object} vehicle - 战车对象
     * @returns {number} 总重量（装备重量 + SP重量）
     */
    static calculateTotalWeightWithSp(vehicle) {
        const equipmentWeight = this.calculateTotalWeight(vehicle);
        const spWeight = (vehicle.sp || 0) / 10; // SP转换为重量（1SP=0.1重量）
        return equipmentWeight + spWeight;
    }
    
    /**
     * 计算战车最大抗腐蚀图层值（基于底盘装备）
     * @param {Object} vehicle - 战车对象
     * @returns {number} 最大抗腐蚀图层值，如果没有底盘或底盘损坏则返回0
     */
    static calculateMaxCorrosionLayer(vehicle) {
        if (!vehicle || !vehicle.equipment || !vehicle.equipment.chassis) {
            return 0;
        }
        
        const chassis = vehicle.equipment.chassis;
        
        // 检查底盘是否损坏
        if (chassis.durability !== undefined && chassis.durability <= 0) {
            return 0;
        }
        
        // 从底盘装备获取最大图层值
        return chassis.maxCorrosionLayer || 0;
    }
    
    /**
     * 计算战车总属性（包括装备加成）
     * @param {Object} vehicle - 战车对象
     * @returns {Object} 总属性
     */
    static calculateVehicleStats(vehicle) {
        if (!vehicle) {
            return {
                attack: 0,
                defense: 0,
                maxSp: 0,
                dodgeRate: 0,
                corrosionResistance: 0,
                corrosionConsumption: 0,
                speedBonus: 0,
                initiativeBonus: 0
            };
        }
        
        // 计算SP最大值（基于载重）
        const maxSp = this.calculateMaxSp(vehicle);
        
        const stats = {
            attack: 0,
            defense: 0,
            maxSp: maxSp,
            dodgeRate: 0,
            corrosionResistance: 0,      // 负数表示减少消耗
            corrosionConsumption: 0,     // 正数表示增加消耗
            speedBonus: 0,
            initiativeBonus: 0
        };
        
        if (!vehicle.equipment) {
            return stats;
        }
        
        // 遍历所有装备，计算属性加成
        for (const slot in vehicle.equipment) {
            const equipment = vehicle.equipment[slot];
            if (!equipment) continue;
            
            // 跳过损坏的装备
            if (equipment.durability !== undefined && equipment.durability <= 0) {
                continue;
            }
            
            // 主炮/副炮/S-E的攻击力
            if (equipment.damage) {
                stats.attack += equipment.damage;
            }
            
            // 底盘的防御力
            if (equipment.defense) {
                stats.defense += equipment.defense;
            }
            
            // 装备效果
            if (equipment.effects) {
                const effects = equipment.effects;
                
                // 注意：SP最大值不再由装备效果决定，而是完全基于发动机载重（载重 * 10）
                // 移除了spBonus和spPenalty的处理
                
                // 闪避率（AI装置）
                if (effects.dodgeRate) {
                    stats.dodgeRate += effects.dodgeRate;
                }
                
                // 抗腐蚀抗性（AI装置，负数表示减少消耗）
                if (effects.corrosionResistance !== undefined) {
                    stats.corrosionResistance += effects.corrosionResistance;
                }
                
                // 抗腐蚀消耗（发动机、底盘，正数表示增加消耗）
                if (effects.corrosionConsumption !== undefined) {
                    stats.corrosionConsumption += effects.corrosionConsumption;
                }
                
                // 速度加成（发动机）
                if (effects.speedBonus) {
                    stats.speedBonus += effects.speedBonus;
                }
                
                // 先手加成（发动机）
                if (effects.initiativeBonus) {
                    stats.initiativeBonus += effects.initiativeBonus;
                }
            }
        }
        
        return stats;
    }
    
    /**
     * 获取战车当前可用的武器（主炮、副炮、S-E）
     * @param {Object} vehicle - 战车对象
     * @returns {Array<Object>} 可用武器列表
     */
    static getAvailableWeapons(vehicle) {
        if (!vehicle || !vehicle.equipment) {
            return [];
        }
        
        const weapons = [];
        const weaponSlots = ['mainCannon', 'subCannon', 'se'];
        
        for (const slot of weaponSlots) {
            const weapon = vehicle.equipment[slot];
            if (weapon && weapon.durability > 0) {
                weapons.push({
                    ...weapon,
                    slot: slot
                });
            }
        }
        
        return weapons;
    }
    
    /**
     * 检查武器是否有弹药
     * @param {Object} weapon - 武器对象
     * @param {Object} vehicle - 战车对象
     * @returns {boolean} 是否有弹药
     */
    static hasAmmo(weapon, vehicle) {
        if (!weapon || !vehicle) {
            return false;
        }
        
        // 副炮不需要弹药
        if (weapon.slot === 'subCannon' || !weapon.ammoType) {
            return true;
        }
        
        // 检查弹药
        if (!vehicle.ammo) {
            return false;
        }
        
        const ammoCount = vehicle.ammo[weapon.ammoType] || 0;
        const consumption = weapon.ammoConsumption || 1;
        
        return ammoCount >= consumption;
    }
    
    /**
     * 消耗弹药
     * @param {Object} weapon - 武器对象
     * @param {Object} vehicle - 战车对象
     * @returns {boolean} 是否成功消耗
     */
    static consumeAmmo(weapon, vehicle) {
        if (!weapon || !vehicle) {
            return false;
        }
        
        // 副炮不需要弹药
        if (weapon.slot === 'subCannon' || !weapon.ammoType) {
            return true;
        }
        
        // 检查是否有弹药
        if (!this.hasAmmo(weapon, vehicle)) {
            return false;
        }
        
        // 消耗弹药
        if (!vehicle.ammo) {
            vehicle.ammo = {};
        }
        
        const consumption = weapon.ammoConsumption || 1;
        vehicle.ammo[weapon.ammoType] = (vehicle.ammo[weapon.ammoType] || 0) - consumption;
        
        if (vehicle.ammo[weapon.ammoType] < 0) {
            vehicle.ammo[weapon.ammoType] = 0;
        }
        
        DebugHelper.log(`VehicleEquipmentSystem: 消耗 ${consumption} 发 ${weapon.ammoType}`);
        
        return true;
    }
}

